import { useEffect } from "react";

type Handler = (e: KeyboardEvent) => void;

// Global keyboard shortcuts. Pass a map of "key" → handler.
// Supports simple chord prefixes via "g+o", "g+u" (sequence within 800ms).
// Ignores presses while focus is in an editable element.
export function useHotkeys(map: Record<string, Handler>): void {
  useEffect(() => {
    let chordPrefix: string | null = null;
    let chordTimer: ReturnType<typeof setTimeout> | null = null;

    const isEditable = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      if (chordPrefix) {
        const combined = `${chordPrefix}+${key}`;
        chordPrefix = null;
        if (chordTimer) clearTimeout(chordTimer);
        const handler = map[combined];
        if (handler) {
          e.preventDefault();
          handler(e);
        }
        return;
      }

      // Direct match
      const direct = map[key];
      if (direct) {
        e.preventDefault();
        direct(e);
        return;
      }

      // Start a chord if any mapped key uses this prefix
      const startsWith = Object.keys(map).some((k) => k.startsWith(`${key}+`));
      if (startsWith) {
        chordPrefix = key;
        if (chordTimer) clearTimeout(chordTimer);
        chordTimer = setTimeout(() => {
          chordPrefix = null;
        }, 800);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (chordTimer) clearTimeout(chordTimer);
    };
  }, [map]);
}
